import React from 'react';

const ViewClientModal = ({ isOpen, client, onClose, isDarkMode }) => {
  if (!isOpen || !client) return null;

  const getStatusBadge = (
    value,
    label,
    trueColor = isDarkMode ? 'bg-emerald-900 text-emerald-200' : 'bg-emerald-100 text-emerald-800',
    falseColor = isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
  ) => (
    <div className={`flex items-center justify-between p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center">
        <div className={`w-3 h-3 rounded-full mr-3 ${value ? 'bg-emerald-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{label}</span>
      </div>
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${value ? trueColor : falseColor}`}>
        {value ? 'Oui' : 'Non'}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 ${
          isDarkMode
            ? 'bg-gray-800' // Match the dark body background
            : 'bg-gradient-to-r from-primary-600 to-primary-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                {client.logo ? (
                  <img
                    src={`data:image/jpeg;base64,${client.logo}`}
                    alt={client.raisonSocial}
                    className="w-full h-full object-contain rounded-xl"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {client.raisonSocial?.split(' ').slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase() || 'CL'}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{client.raisonSocial}</h2>
                <p className="text-primary-100 text-sm">ID: {client.id} • {client.isClient ? "Client Actif" : "Non Client"}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(95vh - 140px)' }}>
          <div className="p-6">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Left Column - Contact Information */}
              <div className="xl:col-span-1 space-y-6">
                {/* Contact Information */}
                <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-red-100 rounded-lg mr-3">
                      <i className="fas fa-address-card text-red-600"></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations de contact</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Email/Login */}
                    <div className={`flex items-center p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                        <i className="fas fa-user text-red-600 text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>LOGIN</p>
                        <p className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm`}>{client.login}</p>
                      </div>
                    </div>

                    {client.nom && (
                      <div className={`flex items-center p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-user-tag text-blue-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>NOM</p>
                          <p className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm`}>{client.nom}</p>
                        </div>
                      </div>
                    )}

                    {client.prenom && (
                      <div className={`flex items-center p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-user-tag text-blue-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>PRÉNOM</p>
                          <p className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm`}>{client.prenom}</p>
                        </div>
                      </div>
                    )}

                    {client.tel && (
                      <div className={`flex items-center p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-phone text-green-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>TÉLÉPHONE</p>
                          <p className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm`}>{client.tel}</p>
                        </div>
                      </div>
                    )}

                    {client.matriculeFiscal && (
                      <div className={`flex items-center p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-file-invoice text-orange-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>MATRICULE FISCAL</p>
                          <p className={`font-semibold break-all ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm`}>{client.matriculeFiscal}</p>
                        </div>
                      </div>
                    )}

                    {client.password && (
                      <div className={`flex items-center p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-key text-gray-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>MOT DE PASSE</p>
                          <p className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm`}>{client.password}</p>
                        </div>
                      </div>
                    )}

                    {client.discriminator && (
                      <div className={`flex items-center p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-user-shield text-gray-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>DISCRIMINATOR</p>
                          <p className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm`}>{client.discriminator}</p>
                        </div>
                      </div>
                    )}

                    {client.locals && (
                      <div className={`flex items-center p-4 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-map-marker-alt text-gray-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>LOCALS</p>
                          <p className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm`}>{client.locals}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Status & Settings */}
              <div className="xl:col-span-2 space-y-6">
                {/* Fiscal Status */}
                <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-green-100 rounded-lg mr-3">
                      <i className="fas fa-cogs text-green-600"></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Statut fiscal</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getStatusBadge(client.timberFiscal, 'Timber Fiscal')}
                    {getStatusBadge(client.fodec, 'FODEC')}
                    {getStatusBadge(client.assujeti, 'Assujetti TVA')}
                    {getStatusBadge(client.exonore, 'Exonéré')}
                    {getStatusBadge(client.annuelle, 'Entretien Annuelle')}
                    {getStatusBadge(!client.annuelle, 'Entretien Semestrielle')}
                  </div>
                </div>

                {/* Observations */}
                {client.observation && (
                  <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-amber-50'}`}>
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-amber-100 rounded-lg mr-3">
                        <i className="fas fa-comment-alt text-amber-600"></i>
                      </div>
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Observations</h3>
                    </div>
                    
                    <div className={`rounded-xl p-6 shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={`leading-relaxed text-lg ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{client.observation}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`border-t px-6 py-4 flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button 
              type="button"
              onClick={onClose}
              className={`px-6 py-3 border rounded-lg transition-colors font-medium ${
                isDarkMode 
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                  : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewClientModal;